import { Form } from "antd";
import _ from "lodash";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes } from "utils/formUtils";

import GenericMappingForm from "../GenericMappingForm";

const CybeatsMappingForm = forwardRef(
  function CybeatsMappingForm({ disableProjectFields = false, ...props }, ref) {
    const IntRef = useRef({});
    const [form] = Form.useForm();
    
    const productFieldName = `cybeatsProductId`;

    const [products, setProducts] = useState(() => {
      if (props.record) {
        const { productName, productType } = props.record;
        return productName
          ? [
              {
                id: productName,
                name: productName,
                otherProperties: { productType },
              },
            ]
          : [];
      }
      return [];
    });

    const [versions, setVersions] = useState(() => {
      if (props.record) {
        const { versions } = props.record;
        const versionsArray = _.isEmpty(versions)
          ? []
          : versions.split(",") || [];
        return versionsArray.map((name) => ({ id: name, name }));
      }
      return [];
    });

    const setOptionsFromResponse = (option, fetchKey) => {
      if (fetchKey === "version") {
        setVersions(option);
      } else {
        setProducts(option);
      }
    };

    const fetchProjects = useCallback(
      (key, params) => {
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            login_id: props.loginConfigId,
            ...params,
          });
        promise.then((response) => {
          const fetchKey = key === "product" ? "product" : "version";
          setOptionsFromResponse(response.projects, fetchKey);
        });

        return { promise, cancel };
      },
      [props.loginConfigId, props.toolName],
    );

    const fetchProduct = useCallback(
      (params) => {
        return fetchProjects("product", params);
      },
      [fetchProjects],
    );

    const fetchVersion = useCallback(
      (params) => {
        return fetchProjects("version", params);
      },
      [fetchProjects],
    );

    const getExtraFieldsData = useCallback(
      (config) => {
        const productId = config[productFieldName];
        const { versionId } = config;

        const product = products.find((p) => p.id === productId);
        const productName = product?.name;
        const productType = product?.otherProperties?.productType;

        const data = versions.reduce(
          (acc, cv) => {
            const { id, name } = cv;
            if (versionId?.includes(id)) {
              acc.id.push(id);
              acc.name.push(name);
            }
            return acc;
          },
          { id: [], name: [] },
        );

        return {
          productId,
          productName,
          productType,
          versionId: data.id.join(","),
          versions: data.name.join(","),
          ...(config.discovery_config_id && {
            discovery_config_id: config.discovery_config_id,
          }),
        };
      },
      [products, versions, productFieldName],
    );

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const { versions, productName, productType } = config;

      const versionsArray = _.isEmpty(versions) ? [] : versions.split(",");

      return {
        [productFieldName]: productName,
        versionId: versionsArray,
        productType,
        ...(config.discovery_config_id && {
          discovery_config_id: config.discovery_config_id,
        }),
      };
    }, [productFieldName]);

    const onValuesChange = useCallback(
      (value, field) => {
        const { name } = field || {};
        const configurations = form.getFieldValue("configurations");
        let updatedConfiguration = [...configurations];
        updatedConfiguration[name] = {
          ...updatedConfiguration[name],
          versionId: undefined,
        };
        form.setFieldsValue({ configurations: updatedConfiguration });
      },
      [form],
    );

    const extraFields = useMemo(
      () => [
        {
          name: productFieldName,
          colSpan: "12",
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          label: `Cybeats Product`,
          disabled: disableProjectFields,
          required: true,
          props: {
            api: fetchProduct,
            allowClear: true,
            onChange: onValuesChange,
            autoFocus: !disableProjectFields,
          },
        },
        {
          name: "versionId",
          colSpan: 12,
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          label: "Version",
          props: {
            setExtraProps: (fieldProps, currentField) => {
              const configurations = form.getFieldValue("configurations");
              const currentConfiguration =
                configurations[currentField.name] || {};
              const productId = currentConfiguration?.[productFieldName];

              if (productId) {
                const product = products.find((p) => p.id === productId);
                if (product && !IntRef.current[productId]) {
                  IntRef.current[productId] = {
                    project: productId,
                    type: product.otherProperties?.productType || "",
                  };
                }
              }

              return {
                mode: "multiple",
                api: productId && fetchVersion,
                allowClear: true,
                apiParams: IntRef.current[productId],
                resetOptionsProp: IntRef.current[productId],
              };
            },
            placeholder: "Leave blank to select all versions",
          },
        },
      ],
      [
        fetchProduct,
        fetchVersion,
        onValuesChange,
        form,
        disableProjectFields,
        products,
        productFieldName,
      ],
    );

    return (
      <GenericMappingForm
        ref={ref}
        form={form}
        getExtraFieldsData={getExtraFieldsData}
        parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        extraFields={extraFields}
        {...props}
      />
    );
  },
);

export default CybeatsMappingForm;